import React from 'react';
import { string } from 'prop-types';
import { styled } from '@mui/system';

const HighlightSpan = styled('span')(() => ({
  backgroundColor: 'yellow'
}));

const escapeRegExp = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const supportsUnicodeProps = (() => {
  try {
    return /\p{L}/u.test('a'); // throws in engines without Unicode property escapes
  } catch {
    return false;
  }
})();

// Treat any non-letter/number as a separator (space, dash, dots, etc.)
const SEP = supportsUnicodeProps ? '[^\\p{L}\\p{N}]' : '[^A-Za-z0-9]';

const makeSearchRegex = raw => {
  const literal = escapeRegExp(raw);
  const flexible = raw.split('').map(ch => escapeRegExp(ch)).join(`${SEP}*`);
  const flags = supportsUnicodeProps ? 'giu' : 'gi';
  // literal OR flexible
  return new RegExp(`(?:${literal})|(?:${flexible})`, flags);
};

/**
 * Highlights text by search word.
 * @returns {React.FC}
 */

const HighlightWord = ({
  searchWord = '',
  textToHighlight = '',
  className = ''
}) => {
  if (!searchWord || !textToHighlight) {
    return <span>{textToHighlight}</span>;
  }

  const q = (searchWord || '').trim();
  if (!q || !textToHighlight) return textToHighlight;

  let regexp;
  try {
    regexp = makeSearchRegex(q);
  } catch {
    return <span>{textToHighlight}</span>;
  }

  const parts = textToHighlight.split(regexp);
  const hits = textToHighlight.match(regexp);
  if (!hits) return <span>{textToHighlight}</span>;

  return parts.map((str, i) => (
    <React.Fragment key={`${i}:${str}`}>
      {str}
      {i < parts.length - 1 && (
        className ?
          <span className={className}>{hits[i] ?? ''}</span> :
          <HighlightSpan>{hits[i] ?? ''}</HighlightSpan>
      )}
    </React.Fragment>
  ));
};

HighlightWord.propTypes = {
  searchWord: string,
  textToHighlight: string,
  className: string
};

export default HighlightWord;
